import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEO from "@/components/feature/SEO";
import SchemaOrg from "@/components/feature/SchemaOrg";
export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim()) {
      setFormStatus("success");
      return;
    }

    const formData = new FormData(form);
    const submitAddr = form.getAttribute("action") || "";

    try {
      const resp = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await resp.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      const isSuccess = resp.ok && parsed?.code === "OK";
      if (isSuccess) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后重试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请稍后重试");
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 51漫画 | 品牌介绍与联系方式"
        description="51漫画品牌介绍，了解我们的漫画资源平台。提供海量免费漫画下载、离线阅读与手机客户端安装服务。查看用户口碑与联系方式。"
        keywords="51漫画关于我们,51漫画品牌,51漫画联系方式,51漫画网站,漫画下载平台"
        canonicalUrl="https://www.51manhua.com/about"
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: "关于我们 - 51漫画",
          description: "了解51漫画品牌、用户口碑与联系方式",
          url: "https://www.51manhua.com/about",
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.51manhua.com/" },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={[{ label: "关于我们" }]} />
            </div>

            <section className="mb-14 md:mb-20">
              <div className="max-w-3xl">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  关于51漫画
                </h1>
                <p className="text-foreground-600 leading-relaxed text-base">
                  51漫画是国内领先的漫画资源下载平台，致力于为广大漫画爱好者提供最全面、最高质量的免费漫画下载服务。
                </p>
              </div>
            </section>

            <section className="mb-14 md:mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed">
                    <p>
                      51漫画成立于2020年，经过多年发展，已成为国内最受欢迎的漫画下载平台之一。我们汇聚了超过10万部各类漫画资源，涵盖热血、恋爱、搞笑、冒险、奇幻、科幻、悬疑、恐怖等十二大分类，满足不同读者的阅读需求。
                    </p>
                    <p>
                      我们的核心优势在于提供<strong>完全免费</strong>的漫画下载服务。用户可以通过51漫画APP或网站免费下载漫画全集，支持离线阅读，无需网络也能随时随地享受漫画阅读的乐趣。
                    </p>
                    <p>
                      51漫画团队由一群热爱漫画的年轻人组成，我们深知漫画爱好者的需求——高清画质、快速更新、便捷下载。为此，我们不断优化产品体验，确保每一位用户都能获得最佳的漫画阅读体验。
                    </p>
                    <p>
                      目前，51漫画已覆盖<strong>iOS和安卓</strong>双平台，累计服务超过<strong>500万</strong>活跃用户，总下载量突破<strong>1亿次</strong>。我们的目标是成为中文互联网最好的漫画资源下载平台。
                    </p>
                  </div>
                </div>
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20bright%20creative%20office%20space%20with%20comic%20manga%20art%20on%20walls%2C%20young%20creative%20team%20working%20on%20tablets%2C%20warm%20natural%20lighting%2C%20colorful%20decor%2C%20casual%20friendly%20atmosphere%2C%20wide%20angle%20photography&width=900&height=675&seq=about-brand-51&orientation=landscape"
                    alt="51漫画团队办公环境"
                    title="51漫画 - 品牌团队"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section className="mb-14 md:mb-20 py-14 md:py-16 bg-background-100/60 -mx-4 md:-mx-6 px-4 md:px-6 rounded-none">
              <div className="max-w-[1400px] mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2 text-center">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">来自真实用户的评价</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      name: "漫画迷小王",
                      rating: 5,
                      text: "51漫画真的太方便了！资源超级全，我想看的漫画基本都能找到。APP下载速度也很快，离线阅读功能在地铁上太实用了，强烈推荐！",
                      date: "2026年7月",
                    },
                    {
                      name: "追漫达人",
                      rating: 5,
                      text: "用了很多漫画APP，51漫画是最好用的。更新速度快，画质清晰，而且完全免费！分类也很详细，找漫画很方便。",
                      date: "2026年7月",
                    },
                    {
                      name: "二次元爱好者",
                      rating: 5,
                      text: "朋友推荐来51漫画下载的，没想到资源这么丰富。热门的漫画基本都有，而且支持全集下载，不用一话一话地点，体验很棒！",
                      date: "2026年6月",
                    },
                    {
                      name: "学生党小林",
                      rating: 4,
                      text: "作为学生党，免费真的很重要。51漫画不仅免费，漫画质量也很高。离线下载功能帮我在没有WiFi的时候也能看漫画。",
                      date: "2026年6月",
                    },
                    {
                      name: "上班族老张",
                      rating: 5,
                      text: "通勤路上必备51漫画APP。资源多、更新快、下载方便，而且还支持手机客户端，安装也很简单。已经推荐给同事了！",
                      date: "2026年5月",
                    },
                    {
                      name: "漫画收藏家",
                      rating: 5,
                      text: "51漫画的分类做得很好，热血、悬疑、搞笑都有独立分类。我现在收藏了上百部漫画，离线阅读完全不占流量。",
                      date: "2026年5月",
                    },
                  ].map((review, i) => (
                    <div
                      key={i}
                      className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, starIdx) => (
                          <i
                            key={starIdx}
                            className={`text-sm ${starIdx < review.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"}`}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        "{review.text}"
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                        <span className="text-xs text-foreground-400">{review.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-14 md:mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    联系我们
                  </h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                        <p className="text-sm text-foreground-500">contact@51manhua.com</p>
                        <p className="text-sm text-foreground-500">support@51manhua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-phone-line text-lg" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800 mb-1">客服电话</h3>
                        <p className="text-sm text-foreground-500">400-888-0000</p>
                        <p className="text-xs text-foreground-400">工作日 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line text-lg" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                        <p className="text-sm text-foreground-500">北京市朝阳区漫画大厦15层</p>
                        <p className="text-xs text-foreground-400">邮编：100000</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-5 rounded-xl bg-background-100/70 border border-background-200/70">
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">商务合作</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      如果您有商务合作、广告投放、版权授权等需求，欢迎通过以上联系方式与我们沟通。我们期待与您的合作！
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    留言反馈
                  </h2>
                  <form
                    data-readdy-form
                    action="https://readdy.ai/api/form/d9mt96cit35ii3jdlll0"
                    onSubmit={handleSubmit}
                    className="space-y-4 p-6 rounded-xl bg-background-100/70 border border-background-200/70"
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full px-5 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                    {formStatus === "success" && (
                      <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === "error" && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}